import React, { useRef, useCallback, useState, useLayoutEffect, useEffect } from 'react'
import clsx from 'clsx'
import { Button } from '@astrid/components'
import { LibraryItem } from 'components/LibraryItem'
import { StoreShelfEntry } from 'store/services/Library'
import { LearningJourneyEntityType } from 'store/services/Journey'
import { ReactComponent as ChevronLeft } from 'resources/graphics/icons/chevron-west.svg'
import { ReactComponent as ChevronRight } from 'resources/graphics/icons/chevron-east.svg'
import { useRovingFocus } from './hooks'

import styles from './ShelfCarousel.module.scss'

type ShelfCarouselProps = {
  itemKeyPrefix: string
  items: StoreShelfEntry[]
  hasAfter: boolean
  onItemClick: (item: StoreShelfEntry) => void
  onEndReached: () => Promise<void>
  shelfRef: (element: HTMLElement | null) => HTMLElement | null
}

export const ShelfCarousel = ({
  items,
  shelfRef,
  itemKeyPrefix,
  onEndReached,
  onItemClick,
  hasAfter
}: ShelfCarouselProps) => {
  const [canScroll, setCanScroll] = useState({
    left: false,
    right: hasAfter
  })
  const scrollableContainerRef = useRef<HTMLDivElement>(null)
  const ticking = useRef(false)

  const { getItemProps, onKeyDown } = useRovingFocus(items.length)

  const keyExtractor = useCallback(
    (item: StoreShelfEntry, index: number) => {
      switch (item.type) {
        case LearningJourneyEntityType.Book:
          return `${itemKeyPrefix}-${item.data.bookId}-${item.data.book.bookType}`
        case LearningJourneyEntityType.LevelUp:
          return `${itemKeyPrefix}-${item.type}-${item.data.subtype}`
        default:
          return `${itemKeyPrefix}-${item.type}-${index}`
      }
    },
    [itemKeyPrefix]
  )

  const updateScrollState = useCallback(() => {
    const scrollableContainer = scrollableContainerRef.current
    if (!scrollableContainer) return

    const { scrollLeft, scrollWidth, clientWidth } = scrollableContainer

    setCanScroll((prev) => {
      const next = {
        left: scrollLeft > 0,
        right: Math.ceil(scrollLeft + clientWidth) < scrollWidth || hasAfter
      }

      if (prev.left === next.left && prev.right === next.right) {
        return prev
      }

      return next
    })

    if (scrollLeft + clientWidth >= scrollWidth - 1 && hasAfter) {
      void onEndReached()
    }
  }, [onEndReached])

  const onScroll = () => {
    if (ticking.current) return

    ticking.current = true

    requestAnimationFrame(() => {
      updateScrollState()
      ticking.current = false
    })
  }

  useLayoutEffect(() => {
    updateScrollState()
  }, [items, updateScrollState])

  useEffect(() => {
    const container = scrollableContainerRef.current
    if (!container) return

    const observer = new ResizeObserver(() => {
      updateScrollState()
    })

    observer.observe(container)

    return () => observer.disconnect()
  }, [updateScrollState])

  const scrollBy = (direction: 'left' | 'right') => {
    const scrollableContainer = scrollableContainerRef.current
    if (!scrollableContainer) return

    const scrollAmount = scrollableContainer.clientWidth * 0.6

    scrollableContainer.scrollBy({
      behavior: 'smooth',
      left: direction === 'left' ? -scrollAmount : scrollAmount
    })
  }

  return (
    <div className={styles.wrapper} ref={shelfRef}>
      {canScroll.left && (
        <Button
          tabIndex={-1}
          aria-label={`button-scroll-${itemKeyPrefix}-left`}
          className={clsx(styles['control-button'], styles.left)}
          onClick={(event) => {
            event.stopPropagation()

            scrollBy('left')
          }}
        >
          <ChevronLeft />
        </Button>
      )}
      <div className={styles.carouselContainer} ref={scrollableContainerRef} onScroll={onScroll}>
        {items.map((item, index) => (
          <div key={keyExtractor(item, index)} className={styles.itemWrapper} onKeyDown={onKeyDown}>
            <LibraryItem item={item} onClick={onItemClick} {...getItemProps(index)} />
          </div>
        ))}
      </div>
      {canScroll.right && (
        <Button
          tabIndex={-1}
          aria-label={`button-scroll-${itemKeyPrefix}-right`}
          className={clsx(styles['control-button'], styles.right)}
          onClick={(event) => {
            event.stopPropagation()

            scrollBy('right')
          }}
        >
          <ChevronRight />
        </Button>
      )}
    </div>
  )
}
